import React, { useState, useEffect, useRef } from 'react';
import { Badge, Dropdown, List, Button, Empty, Spin, Typography, Switch, Tooltip } from 'antd';
import { 
  BellOutlined, 
  CheckOutlined, 
  DeleteOutlined,
  ClockCircleOutlined 
} from '@ant-design/icons';
import styled from 'styled-components';
import NotificationService from '../../services/NotificationService';
import { subscribeToPush, unsubscribeFromPush, getPushStatus } from '../../services/webPush';
import { COLORS } from '../../constants/colors';
import { useNotificationRefresh } from '../../contexts/NotificationContext';

const { Text } = Typography;

const NotificationBellContainer = styled.div`
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background-color 0.3s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const NotificationDropdown = styled.div`
  min-width: 380px;
  max-width: 420px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
`;

const NotificationHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fafafa;
`;

const NotificationTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.textPrimary};
`;

const NotificationList = styled(List)`
  max-height: 400px;
  overflow-y: auto;

  .ant-list-item {
    padding: 12px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
    border-bottom: 1px solid #f0f0f0;

    &:hover {
      background-color: #f5f5f5;
    }

    &.unread {
      background-color: #e6f7ff;
      border-left: 3px solid ${COLORS.$primaryBlue};
    }
  }
`;

const NotificationContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const NotificationTitleText = styled(Text)`
  font-weight: 600;
  font-size: 14px;
  color: ${COLORS.textPrimary};
`;

const NotificationMessage = styled(Text)`
  font-size: 13px;
  color: ${COLORS.textSecondary};
`;

const NotificationTime = styled(Text)`
  font-size: 12px;
  color: ${COLORS.textMuted};
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
`;

const NotificationActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const NotificationFooter = styled.div`
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  text-align: center;
  background-color: #fafafa;
`;

const PushToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #ffffff;
`;

const NotificationBell = ({ enablePolling = false }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [pushStatus, setPushStatus] = useState('unsubscribed');
  const [pushBusy, setPushBusy] = useState(false);
  const prevUnreadCountRef = useRef(0);
  const { triggerRefresh } = useNotificationRefresh();

  useEffect(() => {
    // Only start polling if enablePolling prop is true (e.g., on Intake page)
    let intervalId = null;
    if (enablePolling) {
      intervalId = NotificationService.startPolling(handleUnreadCountUpdate, 60000);
    }

    // Initial load
    loadNotifications();

    // Re-register an existing push subscription (no prompt if not yet granted).
    subscribeToPush().finally(() => {
      getPushStatus().then(setPushStatus);
    });

    return () => {
      if (intervalId) {
        NotificationService.stopPolling(intervalId);
      }
    };
  }, [enablePolling]);

  const handleUnreadCountUpdate = (count) => {
    
    // If unread count increased, trigger a refresh
    if (count > prevUnreadCountRef.current) {
      try {
        triggerRefresh();
      } catch (error) {
        // Silent error handling for refresh callbacks
      }
    }
    
    prevUnreadCountRef.current = count;
    setUnreadCount(count);
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await NotificationService.getNotifications({ limit: 20 });
      // NotificationService already extracts the data array from backend response
      const notificationsList = Array.isArray(response) ? response : (response.data || []);
      setNotifications(notificationsList);
      
      // Update unread count
      const count = await NotificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      try {
        await NotificationService.markAsRead(notification._id);
        await loadNotifications(); // Reload to update UI
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await NotificationService.markAllAsRead();
      await loadNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId, e) => {
    e.stopPropagation(); // Prevent click event from bubbling
    try {
      await NotificationService.deleteNotification(notificationId);
      await loadNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleTogglePush = async (checked) => {
    setPushBusy(true);
    try {
      if (checked) {
        const ok = await subscribeToPush({ requestPermission: true });
        setPushStatus(ok ? 'subscribed' : await getPushStatus());
      } else {
        await unsubscribeFromPush();
        setPushStatus('unsubscribed');
      }
    } finally {
      setPushBusy(false);
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInSeconds = Math.floor((now - notificationDate) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const notificationMenu = (
    <NotificationDropdown>
      <NotificationHeader>
        <NotificationTitle>Notifications</NotificationTitle>
        {unreadCount > 0 && (
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            onClick={handleMarkAllAsRead}
          >
            Mark all read
          </Button>
        )}
      </NotificationHeader>

      {pushStatus !== 'unsupported' && (
        <PushToggleRow>
          <Text style={{ fontSize: 13, color: COLORS.textSecondary }}>
            {pushStatus === 'denied'
              ? 'Push blocked in browser settings'
              : 'Push alerts on this device'}
          </Text>
          <Tooltip
            title={
              pushStatus === 'denied'
                ? 'Notifications are blocked. Enable them in your browser site settings.'
                : pushStatus === 'subscribed'
                  ? 'Turn off push notifications on this device'
                  : 'Turn on push notifications on this device'
            }
          >
            <Switch
              size="small"
              checked={pushStatus === 'subscribed'}
              loading={pushBusy}
              disabled={pushStatus === 'denied'}
              onChange={handleTogglePush}
            />
          </Tooltip>
        </PushToggleRow>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Spin />
        </div>
      ) : notifications.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No notifications"
          style={{ padding: '40px 20px' }}
        />
      ) : (
        <NotificationList
          dataSource={notifications}
          renderItem={(notification) => (
            <List.Item
              className={!notification.isRead ? 'unread' : ''}
              onClick={() => handleNotificationClick(notification)}
            >
              <NotificationContent>
                <NotificationTitleText>{notification.title}</NotificationTitleText>
                <NotificationMessage>{notification.message}</NotificationMessage>
                {notification.patientName && (
                  <Text style={{ fontSize: 12, color: COLORS.textSecondary }}>
                    Patient: {notification.patientName}
                  </Text>
                )}
                <NotificationTime>
                  <ClockCircleOutlined />
                  {formatTimeAgo(notification.createdAt)}
                </NotificationTime>
              </NotificationContent>
              <NotificationActions>
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={(e) => handleDeleteNotification(notification._id, e)}
                  style={{ color: COLORS.textMuted }}
                />
              </NotificationActions>
            </List.Item>
          )}
        />
      )}

      <NotificationFooter>
        <Button type="link" size="small" onClick={loadNotifications}>
          Refresh
        </Button>
      </NotificationFooter>
    </NotificationDropdown>
  );

  const handleVisibleChange = (newVisible) => {
    setVisible(newVisible);
    if (newVisible) {
      // Automatically refresh notifications when dropdown opens

      loadNotifications();

      // Opening the bell is a user gesture — a good moment to ask for push permission.
      subscribeToPush({ requestPermission: true });
    }
  };

  return (
    <Dropdown
      dropdownRender={() => notificationMenu}
      trigger={['click']}
      placement="bottomRight"
      open={visible}
      onOpenChange={handleVisibleChange}
    >
      <NotificationBellContainer>
        <Badge count={unreadCount} overflowCount={99}>
          <BellOutlined style={{ fontSize: 20, color: COLORS.textPrimary }} />
        </Badge>
      </NotificationBellContainer>
    </Dropdown>
  );
};

export default NotificationBell;
